import { encodeQueryParams } from "@hex/common";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import { TAO_PROJECT_VIEW, type TaoProjectView } from "./taoProjectViews";

/** Fresh project from the list; Code/Preview show the centered prompt until the first message. */
export const TAO_PROJECT_NEW_QUERY_PARAM = "new";
export const TAO_PROJECT_PREVIEW_MODE_QUERY_PARAM = "mode";
/** Read only, so links from before Inspect mode still open in Select. */
const LEGACY_SELECT_MODE_QUERY_PARAM = "select";

/** What a click in the app preview does: use the app, select an element, or inspect a cell. */
export type TaoPreviewMode = "preview" | "select" | "inspect";

export type TaoProjectBranchQueryParams = {
  threadId?: string;
  view?: TaoProjectView;
  agentOpen?: boolean;
  newProject?: boolean;
  inspectCellName?: string;
  previewMode?: TaoPreviewMode;
};

interface TaoProjectViewState {
  selectedView: TaoProjectView;
  agentOpen: boolean;
  newProject: boolean;
  inspectCellName: string | null;
  previewMode: TaoPreviewMode;
}

export interface TaoProjectChangeViewOptions {
  previewMode?: TaoPreviewMode;
}

export function useTaoProjectViewState(): TaoProjectViewState & {
  changeView: (
    view: TaoProjectView,
    options?: TaoProjectChangeViewOptions,
  ) => void;
  /** Whether the app is in Select mode; derived from `previewMode`. */
  selectMode: boolean;
  setPreviewMode: (previewMode: TaoPreviewMode) => void;
  openInspect: (cellName: string) => void;
  closeInspect: () => void;
  clearNewProject: (options?: { agentOpen?: boolean }) => void;
  openAgent: () => void;
  toggleAgent: () => void;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const state = useMemo(
    () => parseTaoProjectViewState(new URLSearchParams(location.search)),
    [location.search],
  );

  const writeState = useCallback(
    (
      nextState: TaoProjectViewState,
      { openedInspect = false, replace = true } = {},
    ) => {
      const params = new URLSearchParams(location.search);
      setTaoProjectViewParams(params, nextState);
      void navigate(
        { search: params.toString() },
        {
          replace,
          state: {
            ...location.state,
            taoInspectOpenedInApp:
              nextState.inspectCellName != null &&
              (openedInspect || location.state?.taoInspectOpenedInApp === true),
          },
        },
      );
    },
    [location.search, location.state, navigate],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const originalSearch = params.toString();
    setTaoProjectViewParams(params, state);
    if (params.toString() !== originalSearch) {
      writeState(state);
    }
  }, [location.search, state, writeState]);

  const changeView = useCallback(
    (selectedView: TaoProjectView, options?: TaoProjectChangeViewOptions) => {
      writeState({
        ...state,
        selectedView,
        inspectCellName: null,
        agentOpen: selectedView === TAO_PROJECT_VIEW.chat || state.agentOpen,
        previewMode: !showsPreview(selectedView)
          ? "preview"
          : (options?.previewMode ??
            (state.previewMode !== "preview"
              ? state.previewMode
              : selectedView === TAO_PROJECT_VIEW.split &&
                  state.selectedView === TAO_PROJECT_VIEW.preview
                ? "select"
                : "preview")),
      });
    },
    [state, writeState],
  );
  const setPreviewMode = useCallback(
    (previewMode: TaoPreviewMode) => {
      if (
        !showsPreview(state.selectedView) ||
        previewMode === state.previewMode
      ) {
        return;
      }
      writeState({ ...state, previewMode });
    },
    [state, writeState],
  );
  const openInspect = useCallback(
    (cellName: string) => {
      if (!cellName || cellName === state.inspectCellName) return;
      writeState(
        {
          ...state,
          selectedView:
            state.selectedView === TAO_PROJECT_VIEW.chat
              ? TAO_PROJECT_VIEW.preview
              : state.selectedView,
          inspectCellName: cellName,
        },
        {
          replace: state.inspectCellName != null,
          openedInspect: state.inspectCellName == null,
        },
      );
    },
    [state, writeState],
  );
  const closeInspect = useCallback(() => {
    if (state.inspectCellName == null) return;
    if (location.state?.taoInspectOpenedInApp === true) {
      void navigate(-1);
    } else {
      writeState({ ...state, inspectCellName: null });
    }
  }, [location.state, navigate, state, writeState]);
  const clearNewProject = useCallback(
    (options?: { agentOpen?: boolean }) => {
      if (!state.newProject) return;
      writeState({
        ...state,
        agentOpen: options?.agentOpen ?? state.agentOpen,
        newProject: false,
      });
    },
    [state, writeState],
  );
  const openAgent = useCallback(() => {
    if (!state.agentOpen) {
      writeState({ ...state, agentOpen: true });
    }
  }, [state, writeState]);
  const toggleAgent = useCallback(() => {
    if (state.selectedView !== TAO_PROJECT_VIEW.chat) {
      writeState({ ...state, agentOpen: !state.agentOpen });
    }
  }, [state, writeState]);

  return {
    ...state,
    selectMode: state.previewMode === "select",
    changeView,
    setPreviewMode,
    openInspect,
    closeInspect,
    clearNewProject,
    openAgent,
    toggleAgent,
  };
}

function showsPreview(view: TaoProjectView): boolean {
  return view === TAO_PROJECT_VIEW.preview || view === TAO_PROJECT_VIEW.split;
}

function parsePreviewMode(params: URLSearchParams): TaoPreviewMode {
  const mode = params.get(TAO_PROJECT_PREVIEW_MODE_QUERY_PARAM);
  if (mode === "select" || mode === "inspect") return mode;
  return params.get(LEGACY_SELECT_MODE_QUERY_PARAM) === "1"
    ? "select"
    : "preview";
}

function parseView(value: string | null): TaoProjectView | null {
  switch (value) {
    case "notebook":
    case TAO_PROJECT_VIEW.code:
      return TAO_PROJECT_VIEW.code;
    case TAO_PROJECT_VIEW.chat:
    case TAO_PROJECT_VIEW.files:
    case TAO_PROJECT_VIEW.preview:
    case TAO_PROJECT_VIEW.split:
      return value;
    default:
      return null;
  }
}

function parseTaoProjectViewState(
  params: URLSearchParams,
): TaoProjectViewState {
  const inspectCellName = params.get("inspect") || null;
  const legacyMainView = parseView(params.get("s1"));
  const legacySecondView = parseView(params.get("s2"));
  const selectedView = params.has("view")
    ? (parseView(params.get("view")) ?? TAO_PROJECT_VIEW.chat)
    : legacyMainView === TAO_PROJECT_VIEW.chat
      ? (legacySecondView ?? legacyMainView)
      : (legacyMainView ??
        (inspectCellName != null
          ? TAO_PROJECT_VIEW.preview
          : TAO_PROJECT_VIEW.chat));
  const legacyAgentOpen =
    legacyMainView == null ||
    legacyMainView === TAO_PROJECT_VIEW.chat ||
    legacySecondView === TAO_PROJECT_VIEW.chat;
  return {
    selectedView,
    inspectCellName:
      selectedView === TAO_PROJECT_VIEW.chat ? null : inspectCellName,
    agentOpen:
      selectedView === TAO_PROJECT_VIEW.chat ||
      (params.has("agent")
        ? params.get("agent") !== "0"
        : params.has("view") || legacyAgentOpen),
    newProject: params.get(TAO_PROJECT_NEW_QUERY_PARAM) === "1",
    previewMode: showsPreview(selectedView)
      ? parsePreviewMode(params)
      : "preview",
  };
}

function setTaoProjectViewParams(
  params: URLSearchParams,
  state: TaoProjectViewState,
): void {
  if (state.inspectCellName && state.selectedView !== TAO_PROJECT_VIEW.chat) {
    params.set("inspect", state.inspectCellName);
  } else {
    params.delete("inspect");
  }
  params.delete("s1");
  params.delete("s2");
  if (state.selectedView === TAO_PROJECT_VIEW.chat) {
    params.delete("view");
  } else {
    params.set("view", state.selectedView);
  }
  if (state.agentOpen || state.selectedView === TAO_PROJECT_VIEW.chat) {
    params.delete("agent");
  } else {
    params.set("agent", "0");
  }
  if (state.newProject) {
    params.set(TAO_PROJECT_NEW_QUERY_PARAM, "1");
  } else {
    params.delete(TAO_PROJECT_NEW_QUERY_PARAM);
  }
  params.delete(LEGACY_SELECT_MODE_QUERY_PARAM);
  if (showsPreview(state.selectedView) && state.previewMode !== "preview") {
    params.set(TAO_PROJECT_PREVIEW_MODE_QUERY_PARAM, state.previewMode);
  } else {
    params.delete(TAO_PROJECT_PREVIEW_MODE_QUERY_PARAM);
  }
}

export function taoProjectBranchQueryString(
  queryParams?: TaoProjectBranchQueryParams,
): string {
  const params = new URLSearchParams();
  if (queryParams?.threadId != null) {
    params.set("threadId", queryParams.threadId);
  }
  setTaoProjectViewParams(params, {
    selectedView:
      queryParams?.view ??
      (queryParams?.inspectCellName
        ? TAO_PROJECT_VIEW.preview
        : TAO_PROJECT_VIEW.chat),
    inspectCellName: queryParams?.inspectCellName ?? null,
    agentOpen: queryParams?.agentOpen ?? true,
    newProject: queryParams?.newProject ?? false,
    previewMode: queryParams?.previewMode ?? "preview",
  });
  return encodeQueryParams(Object.fromEntries(params.entries()));
}
